import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const emptyProject = {
    title: '',
    slug: '',
    description: '',
    tags: [],
    gallery: [],
    products_used: [],
    is_published: false,
  };

  const [form, setForm] = useState(emptyProject);
  const [tagsInput, setTagsInput] = useState('');
  const [galleryInput, setGalleryInput] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/projects`);
      setProjects(res.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const data = {
        ...form,
        tags: tagsInput.split(',').map(t => t.trim()).filter(Boolean),
        gallery: galleryInput.split('\n').map(u => u.trim()).filter(Boolean),
      };

      if (editingProject) {
        await axios.put(`${API}/admin/projects/${editingProject.id}`, data);
        toast.success('Project updated');
      } else {
        await axios.post(`${API}/admin/projects`, data);
        toast.success('Project created');
      }
      setDialogOpen(false);
      setEditingProject(null);
      setForm(emptyProject);
      setTagsInput('');
      setGalleryInput('');
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save project');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API}/admin/projects/${id}`);
      toast.success('Project deleted');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  const openEdit = (project) => {
    setEditingProject(project);
    setForm(project);
    setTagsInput(project.tags?.join(', ') || '');
    setGalleryInput(project.gallery?.join('\n') || '');
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditingProject(null);
    setForm(emptyProject);
    setTagsInput('');
    setGalleryInput('');
    setDialogOpen(true);
  };

  const togglePublish = async (project) => {
    try {
      await axios.put(`${API}/admin/projects/${project.id}`, {
        is_published: !project.is_published,
      });
      toast.success(project.is_published ? 'Project unpublished' : 'Project published');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to update project');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-projects">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">
            Projects
          </h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-[#78BE20] text-black hover:bg-[#65a31b]">
                <Plus className="h-4 w-4 mr-2" />
                Add Project
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingProject ? 'Edit Project' : 'Add Project'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    rows={4}
                  />
                </div>

                <div>
                  <Label>Tags (comma separated)</Label>
                  <Input
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    placeholder="Solar, Industrial, Networking"
                  />
                </div>

                <div>
                  <Label>Gallery URLs (one per line)</Label>
                  <Textarea
                    value={galleryInput}
                    onChange={(e) => setGalleryInput(e.target.value)}
                    className="mt-1 bg-zinc-800 border-zinc-700"
                    rows={3}
                    placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label>Published</Label>
                  <Switch
                    checked={form.is_published}
                    onCheckedChange={(checked) => setForm({ ...form, is_published: checked })}
                  />
                </div>

                <Button onClick={handleSave} className="w-full bg-[#78BE20] text-black hover:bg-[#65a31b]">
                  {editingProject ? 'Update Project' : 'Create Project'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#18181b] border border-zinc-800 rounded-sm animate-pulse">
                <div className="aspect-video bg-zinc-800" />
                <div className="p-4 space-y-2">
                  <div className="h-5 bg-zinc-800 rounded w-2/3" />
                  <div className="h-4 bg-zinc-800 rounded w-full" />
                </div>
              </div>
            ))
          ) : projects.length === 0 ? (
            <div className="col-span-full text-center py-12 text-zinc-500">
              No projects yet
            </div>
          ) : (
            projects.map(project => (
              <div key={project.id} className="bg-[#18181b] border border-zinc-800 rounded-sm overflow-hidden">
                {project.gallery?.[0] && (
                  <div className="aspect-video relative">
                    <img src={project.gallery[0]} alt="" className="w-full h-full object-cover" />
                    {!project.is_published && (
                      <div className="absolute top-2 right-2">
                        <Badge className="bg-zinc-700 text-zinc-400">Draft</Badge>
                      </div>
                    )}
                  </div>
                )}
                <div className="p-4">
                  <h3 className="text-white font-bold mb-2">{project.title}</h3>
                  <p className="text-zinc-400 text-sm line-clamp-2 mb-4">{project.description}</p>
                  <div className="flex items-center justify-between">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => togglePublish(project)}
                      className="text-zinc-400 hover:text-white"
                    >
                      {project.is_published ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                      {project.is_published ? 'Unpublish' : 'Publish'}
                    </Button>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(project)} className="text-zinc-400 hover:text-white">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(project.id)} className="text-zinc-400 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
